/**
 * Slide-level PowerPoint tools: presentation info, slide listing/reading,
 * add/delete/move/duplicate, and slide background color.
 *
 * Handlers follow the PowerPoint.run/load/sync conventions documented in
 * pptx-tools.ts.
 */

import type { PptxTool } from "../tool-types";

export const slideTools: PptxTool[] = [
  // ─── Presentation Info ────────────────────────────────────────────────────

  {
    schema: {
      name: "get_presentation_info",
      description:
        "Get high-level information about the current PowerPoint presentation: its title and total slide count.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return PowerPoint.run(async (context) => {
        const presentation = context.presentation;
        presentation.load("title");
        const slides = presentation.slides;
        slides.load("items");

        await context.sync();

        return {
          title: presentation.title,
          slideCount: slides.items.length,
        };
      });
    },
  },

  // ─── Slide Listing ────────────────────────────────────────────────────────

  {
    schema: {
      name: "list_slides",
      description:
        "List all slides in the presentation. Returns each slide's index (0-based) and ID.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items/id");

        await context.sync();

        return {
          slides: slides.items.map((slide, index) => ({
            index,
            id: slide.id,
          })),
        };
      });
    },
  },

  // ─── Get Slide ────────────────────────────────────────────────────────────

  {
    schema: {
      name: "get_slide",
      description:
        "Get the contents of a specific slide by its 0-based index. Returns all shapes with their names, IDs, types, position/size (left, top, width, height in points), text content, fill colour, and basic font info — enough to verify formatting without a screenshot.",
      parameters: {
        type: "object",
        properties: {
          index: {
            type: "number",
            description: "0-based index of the slide to read.",
          },
        },
        required: ["index"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.index as number);
        const shapes = slide.shapes;
        shapes.load(
          "items/id,items/name,items/type,items/left,items/top,items/width,items/height," +
            "items/fill/type,items/fill/foregroundColor"
        );

        await context.sync();

        // Use getTextFrameOrNullObject() so shapes without a text frame
        // (images, charts, groups) return isNullObject=true instead of
        // throwing InvalidArgument at context.sync() time. Font basics load
        // through the same null-object-safe path so the AI can verify
        // formatting without a screenshot round-trip.
        const textFrames = shapes.items.map((shape) =>
          shape.getTextFrameOrNullObject()
        );
        textFrames.forEach((tf) =>
          tf.load(
            "isNullObject,textRange/text," +
              "textRange/font/size,textRange/font/name,textRange/font/color,textRange/font/bold"
          )
        );

        await context.sync();

        return {
          index: args.index as number,
          shapes: shapes.items.map((shape, i) => {
            const tf = textFrames[i];
            const text = tf.isNullObject ? null : tf.textRange.text;
            const font = tf.isNullObject
              ? null
              : {
                  size: tf.textRange.font.size,
                  name: tf.textRange.font.name,
                  color: tf.textRange.font.color,
                  bold: tf.textRange.font.bold,
                };
            return {
              index: i,
              id: shape.id,
              name: shape.name,
              type: shape.type,
              left: shape.left,
              top: shape.top,
              width: shape.width,
              height: shape.height,
              text,
              fill: { type: shape.fill.type, color: shape.fill.foregroundColor },
              font,
            };
          }),
        };
      });
    },
  },

  // ─── Get Selected Slide ───────────────────────────────────────────────────

  {
    schema: {
      name: "get_selected_slide",
      description:
        "Get the currently selected slide in the presentation, INCLUDING its 0-based slideIndex — use that index for follow-up edits when the user says 'this slide'. Returns shapes with their names, IDs, types, position/size (left, top, width, height in points), and text.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return PowerPoint.run(async (context) => {
        const selectedSlides = context.presentation.getSelectedSlides();
        selectedSlides.load("items/id");

        await context.sync();

        if (selectedSlides.items.length === 0) {
          return { selectedSlides: [] };
        }

        const firstSlide = selectedSlides.items[0];
        // Resolve the selected slide's index — the write tools are index-
        // addressed, so the model needs it to edit "this slide" correctly.
        const allSlides = context.presentation.slides;
        allSlides.load("items/id");
        const shapes = firstSlide.shapes;
        shapes.load("items/id,items/name,items/type,items/left,items/top,items/width,items/height");

        await context.sync();

        const slideIndex = allSlides.items.findIndex((s) => s.id === firstSlide.id);

        const textFrames = shapes.items.map((shape) =>
          shape.getTextFrameOrNullObject()
        );
        textFrames.forEach((tf) => tf.load("isNullObject,textRange/text"));

        await context.sync();

        return {
          selectedSlideIds: selectedSlides.items.map((s) => s.id),
          slideIndex,
          totalSlides: allSlides.items.length,
          firstSlide: {
            id: firstSlide.id,
            slideIndex,
            shapes: shapes.items.map((shape, i) => {
              const tf = textFrames[i];
              const text = tf.isNullObject ? null : tf.textRange.text;
              return {
                index: i,
                id: shape.id,
                name: shape.name,
                type: shape.type,
                left: shape.left,
                top: shape.top,
                width: shape.width,
                height: shape.height,
                text,
              };
            }),
          },
        };
      });
    },
  },

  // ─── Add Slide ────────────────────────────────────────────────────────────

  {
    schema: {
      name: "add_slide",
      description: "Add a new blank slide at the end of the presentation.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return PowerPoint.run(async (context) => {
        context.presentation.slides.add();

        await context.sync();

        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        return {
          success: true,
          newSlideCount: slides.items.length,
          newSlideIndex: slides.items.length - 1,
        };
      });
    },
  },

  // ─── Delete Slide ─────────────────────────────────────────────────────────

  {
    schema: {
      name: "delete_slide",
      description:
        "Delete a slide at the given 0-based index. This operation is permanent.",
      parameters: {
        type: "object",
        properties: {
          index: {
            type: "number",
            description: "0-based index of the slide to delete.",
          },
        },
        required: ["index"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.index as number);
        slide.delete();

        await context.sync();

        return { success: true };
      });
    },
  },

  // ─── Move Slide ───────────────────────────────────────────────────────────

  {
    schema: {
      name: "move_slide",
      description:
        "Move a slide to a new 0-based position in the presentation.",
      parameters: {
        type: "object",
        properties: {
          fromIndex: {
            type: "number",
            description: "0-based index of the slide to move.",
          },
          toIndex: {
            type: "number",
            description: "0-based destination index.",
          },
        },
        required: ["fromIndex", "toIndex"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        const fromIndex = args.fromIndex as number;
        const toIndex = args.toIndex as number;

        if (fromIndex < 0 || fromIndex >= slides.items.length) {
          throw new Error(`fromIndex ${fromIndex} is out of range (0–${slides.items.length - 1})`);
        }
        if (toIndex < 0 || toIndex >= slides.items.length) {
          throw new Error(`toIndex ${toIndex} is out of range (0–${slides.items.length - 1})`);
        }

        const slideToMove = slides.items[fromIndex];
        slideToMove.moveTo(toIndex);

        await context.sync();

        return { success: true, movedFrom: fromIndex, movedTo: toIndex };
      });
    },
  },

  // ─── Duplicate Slide ──────────────────────────────────────────────────────

  {
    // `insertSlidesFromBase64` is PowerPointApi 1.2 — the reference gated tool
    // (every modern host supports 1.2, so this is a no-op in practice; it
    // proves the gating path for the 1.4/1.8 tools coming in PPT1).
    minRequirementSet: "1.2",
    schema: {
      name: "duplicate_slide",
      description:
        "Duplicate a slide by its 0-based index. The duplicate is inserted after the existing slides.",
      parameters: {
        type: "object",
        properties: {
          index: {
            type: "number",
            description: "0-based index of the slide to duplicate.",
          },
        },
        required: ["index"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items/id");
        await context.sync();

        const sourceSlide = slides.items[args.index as number];
        const sourceId = sourceSlide.id;

        // Export just this slide as base64, then re-insert it
        const base64Result = sourceSlide.exportAsBase64();
        await context.sync();

        context.presentation.insertSlidesFromBase64(base64Result.value, {
          sourceSlideIds: [sourceId],
          formatting: "UseDestinationTheme",
        });

        await context.sync();

        return { success: true, duplicatedSlideIndex: args.index as number };
      });
    },
  },

  // ─── Apply Background Color ───────────────────────────────────────────────

  {
    schema: {
      name: "apply_background_color",
      description:
        "Apply a solid background color to a slide.",
      parameters: {
        type: "object",
        properties: {
          index: {
            type: "number",
            description: "0-based index of the slide.",
          },
          color: {
            type: "string",
            description: "Hex color string, e.g. '#FF5733' or '#FFFFFF'.",
          },
        },
        required: ["index", "color"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.index as number);
        slide.background.fill.setSolidFill({ color: args.color as string });

        await context.sync();

        return { success: true };
      });
    },
  },
];
